import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import apiEndPointsConfig from '../../../services/apiEndPoints';

const STATUS_COLOR = {
  Completed: 'success',
  completed: 'success',
  partial: 'warning',
  failed: 'error',
  Failed: 'error',
  processing: 'info',
  Processing: 'info'
};

const LANGUAGES = [['en-IN', 'English (Indian)'], ['hi-IN', 'Hindi'], ['gu-IN', 'Gujarati'], ['bn-IN', 'Bengali'], ['mr-IN', 'Marathi'], ['ta-IN', 'Tamil'], ['te-IN', 'Telugu'], ['kn-IN', 'Kannada']];
const AUDIO_EXTENSIONS = new Set(['.wav', '.mp3', '.mpeg', '.m4a', '.mp4', '.aac', '.flac', '.ogg', '.wma', '.aiff', '.alac', '.opus']);

const createInitialForm = () => ({
  fullName: '',
  email: '',
  mobile: '',
  currentEmployer: '',
  currentRole: '',
  location: '',
  totalExperience: '',
  notes: ''
});

const formatDateTime = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
};

const formatStatusLabel = (value = '') => {
  if (!value) return 'Unknown';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const renderArray = (items) => {
  if (!Array.isArray(items) || !items.length) return 'Not available';
  return items.join(', ');
};

const getCandidateRowsFromListResponse = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

export default function NipponResumeUpload({ onMessage, onComplete }) {
  const [form, setForm] = useState(createInitialForm());
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [submittingFlow, setSubmittingFlow] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [language, setLanguage] = useState('en-IN');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const resumeInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const token = localStorage.getItem('token');

  const selectedCandidate = useMemo(
    () => candidates.find((candidate) => candidate.id === selectedCandidateId) || null,
    [candidates, selectedCandidateId]
  );

  const loadCandidates = async (preferredCandidateId = '') => {
    setLoadingCandidates(true);
    try {
      const response = await axiosInstance.get(nipponBotApiEndpoints.candidateList, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const items = getCandidateRowsFromListResponse(response);
      setCandidates(items);
      setSelectedCandidateId(preferredCandidateId || items[0]?.id || '');
    } catch (error) {
      onMessage(error.response?.data?.message || 'Failed to load candidate profiles.', 'error');
    } finally {
      setLoadingCandidates(false);
    }
  };

  useEffect(() => {
    loadCandidates();
  }, []);

  const handleChange = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const chooseResume = (event) => {
    setResumeFile(event.target.files?.[0] || null);
  };

  const chooseAudioFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    const valid = selected.filter((file) => AUDIO_EXTENSIONS.has(file.name.slice(file.name.lastIndexOf('.')).toLowerCase()));
    if (valid.length !== selected.length) {
      onMessage('Unsupported files were removed. Please select audio files only.', 'warning');
    }
    setAudioFiles(valid);
  };

  const resetFlowForm = () => {
    setForm(createInitialForm());
    setResumeFile(null);
    setAudioFiles([]);
    if (resumeInputRef.current) resumeInputRef.current.value = '';
    if (audioInputRef.current) audioInputRef.current.value = '';
  };

  const createOrReuseCandidate = async () => {
    try {
      const candidateResponse = await axiosInstance.post(nipponBotApiEndpoints.candidateCreate, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return candidateResponse.data?.data?.id || '';
    } catch (error) {
      const existingCandidate = error.response?.data?.data;
      if (existingCandidate?.id) {
        onMessage('Candidate already exists for this client. Reusing the existing candidate and continuing the linked upload flow.', 'info');
        return existingCandidate.id;
      }
      throw error;
    }
  };

  const submitCombinedFlow = async (event) => {
    event.preventDefault();

    if (!form.fullName.trim()) return onMessage('Candidate full name is required.', 'error');
    if (!form.email.trim() && !form.mobile.trim()) return onMessage('Candidate email or mobile is required.', 'error');
    if (!resumeFile) return onMessage('Resume file is required for the combined flow.', 'error');
    if (!audioFiles.length) return onMessage('At least one interview audio file is required for the combined flow.', 'error');

    setSubmittingFlow(true);
    let candidateId = '';

    try {
      candidateId = await createOrReuseCandidate();

      if (!candidateId) {
        throw new Error('Candidate was not created and no reusable candidate id was returned.');
      }

      const resumeFormData = new FormData();
      resumeFormData.append('resume', resumeFile);
      resumeFormData.append('candidateId', candidateId);
      const resumeResponse = await axiosInstance.post(nipponBotApiEndpoints.resumeUpload, resumeFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const extractionCompleted = resumeResponse.data?.data?.extractionCompleted !== false;

      const audioFormData = new FormData();
      audioFiles.forEach((file) => audioFormData.append('files', file));
      audioFormData.append('language', language);
      audioFormData.append('date', date);
      audioFormData.append('candidateId', candidateId);
      await axiosInstance.post(apiEndPointsConfig.upload, audioFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      await loadCandidates(candidateId);
      resetFlowForm();
      if (extractionCompleted) {
        onMessage('Candidate, resume, and recording were linked and uploaded successfully.', 'success');
      } else {
        onMessage('Candidate, resume, and recording were uploaded and linked. Resume extraction could not be completed, so please fill the candidate details manually.', 'warning');
      }
      if (typeof onComplete === 'function') onComplete();
    } catch (error) {
      if (candidateId) {
        await loadCandidates(candidateId);
      }
      onMessage(error.response?.data?.status?.value || error.response?.data?.message || error.message || 'Combined candidate onboarding failed.', 'error');
    } finally {
      setSubmittingFlow(false);
    }
  };

  const profile = selectedCandidate?.resume?.structuredProfile || {};

  return (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Candidate Resume And Interview Workspace</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This is now a single linked onboarding flow. Fill candidate details, attach the resume, attach the interview recording, and submit once so all three are connected automatically.
      </Typography>

      <Paper component="form" onSubmit={submitCombinedFlow} variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Single Candidate Onboarding Flow</Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mb: 2 }}>
          <TextField label="Full Name" value={form.fullName} onChange={handleChange('fullName')} fullWidth />
          <TextField label="Email" value={form.email} onChange={handleChange('email')} fullWidth />
          <TextField label="Mobile" value={form.mobile} onChange={handleChange('mobile')} fullWidth />
          <TextField label="Current Employer" value={form.currentEmployer} onChange={handleChange('currentEmployer')} fullWidth />
          <TextField label="Current Role" value={form.currentRole} onChange={handleChange('currentRole')} fullWidth />
          <TextField label="Location" value={form.location} onChange={handleChange('location')} fullWidth />
          <TextField label="Total Experience" value={form.totalExperience} onChange={handleChange('totalExperience')} fullWidth />
          <TextField label="Notes" value={form.notes} onChange={handleChange('notes')} fullWidth />
        </Box>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mb: 2 }}>
          <Button variant="outlined" component="label" sx={{ textTransform: 'none', justifyContent: 'flex-start', minHeight: 56 }}>
            {resumeFile ? `Resume: ${resumeFile.name}` : 'Choose Resume'}
            <input ref={resumeInputRef} hidden type="file" accept=".pdf,.docx,.png,.jpg,.jpeg,.webp" onChange={chooseResume} />
          </Button>
          <Button variant="outlined" component="label" sx={{ textTransform: 'none', justifyContent: 'flex-start', minHeight: 56 }}>
            {audioFiles.length ? `Recording: ${audioFiles.length} file(s) selected` : 'Choose Interview Recording'}
            <input ref={audioInputRef} hidden type="file" multiple onChange={chooseAudioFiles} />
          </Button>
          <TextField select fullWidth label="Audio language" value={language} onChange={(event) => setLanguage(event.target.value)}>
            {LANGUAGES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
          <TextField fullWidth type="date" label="Interview date" value={date} onChange={(event) => setDate(event.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          One submit will create the candidate, upload the resume for extraction, and upload the recording under the same candidate link.
        </Alert>

        <Button type="submit" variant="contained" disabled={submittingFlow} sx={{ textTransform: 'none' }}>
          {submittingFlow ? <><CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />Linking And Uploading...</> : 'Create Candidate And Link Resume + Recording'}
        </Button>
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Saved Linked Candidates</Typography>
          {loadingCandidates ? <CircularProgress size={18} /> : null}
        </Stack>
        <TextField
          select
          fullWidth
          label="Select Candidate"
          value={selectedCandidateId}
          onChange={(event) => setSelectedCandidateId(event.target.value)}
          sx={{ mb: 2 }}
        >
          {candidates.map((candidate) => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.fullName || 'Unnamed Candidate'}
            </MenuItem>
          ))}
        </TextField>

        {!selectedCandidate ? <Alert severity="info">No linked candidate available yet.</Alert> : (
          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' } }}>
            <Stack spacing={1}>
              <Typography variant="body2"><strong>Name:</strong> {selectedCandidate.fullName || 'Not available'}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {selectedCandidate.email || 'Not available'}</Typography>
              <Typography variant="body2"><strong>Current Employer:</strong> {selectedCandidate.currentEmployer || 'Not available'}</Typography>
              <Typography variant="body2"><strong>Current Role:</strong> {selectedCandidate.currentRole || 'Not available'}</Typography>
              <Typography variant="body2">
                <strong>Resume Status:</strong>{' '}
                <Chip size="small" label={selectedCandidate.resumeExtractionStatus ? formatStatusLabel(selectedCandidate.resumeExtractionStatus) : 'Not Uploaded'} color={STATUS_COLOR[selectedCandidate.resumeExtractionStatus] || 'default'} />
              </Typography>
              <Typography variant="caption" color="text.secondary">Created {formatDateTime(selectedCandidate.createdAt)}</Typography>
            </Stack>

            <Stack spacing={1.1}>
              {!selectedCandidate?.resume ? <Alert severity="info">Resume details will appear here after extraction.</Alert> : (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={formatStatusLabel(selectedCandidate.resume.extractionStatus)} color={STATUS_COLOR[selectedCandidate.resume.extractionStatus] || 'default'} />
                    <Typography variant="caption" color="text.secondary">Uploaded {formatDateTime(selectedCandidate.resume.createdAt)}</Typography>
                    <Typography variant="caption" color="text.secondary">Extracted {formatDateTime(selectedCandidate.resume.extractedAt)}</Typography>
                  </Stack>
                  <Typography variant="body2"><strong>Name:</strong> {profile.fullName || 'Not available'}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {profile.email || 'Not available'}</Typography>
                  <Typography variant="body2"><strong>Current Employer:</strong> {profile.currentEmployer || 'Not available'}</Typography>
                  <Typography variant="body2"><strong>Current Role:</strong> {profile.currentRole || 'Not available'}</Typography>
                  <Typography variant="body2"><strong>Total Experience:</strong> {profile.totalExperience || 'Not available'}</Typography>
                  <Typography variant="body2"><strong>Skills:</strong> {renderArray(profile.skills)}</Typography>
                </>
              )}
            </Stack>
          </Box>
        )}
      </Paper>
    </Paper>
  );
}
